/**
 * Which flights a user sees.
 *
 * **The server applies this, never the browser.** Every endpoint that returns flight
 * data answers inside the signed-in user's access, which the server reads from the
 * database on every request. So nothing in this app filters rows by it, and nothing
 * sends it anywhere: it is shown — to an administrator who sets it, and to the user
 * it narrows — and that is all.
 *
 * The rule, in one place:
 *
 * - `restricted: false` — every flight. The default, and what a user nobody has ever
 *   restricted has.
 * - `restricted: true` — only the flights that match BOTH lists. `airports` matches a
 *   flight that departs from or arrives at one of them; `airlines` matches the
 *   carrier. An empty list is no condition on that attribute, and the server refuses
 *   a restriction with both lists empty.
 *
 * An administrator always sees every flight. A restriction stored for one is kept but
 * ignored while the role is ADMIN, and applies again if the role is taken away.
 */
export interface FlightAccess {
  /** `UserInfo.id`. */
  userId: number;
  restricted: boolean;
  /** IATA airport codes, upper case, sorted. Always empty when not restricted. */
  airports: string[];
  /** IATA airline codes, upper case, sorted. Always empty when not restricted. */
  airlines: string[];
  /** `yyyy-MM-dd HH:mm:ss`. `null` — never set, the user has the default. */
  updated: string | null;
  /** The administrator who last changed it. `null` — never set. */
  updatedBy: string | null;
}

/**
 * The body of `POST /user/flight-access/id/{userId}`.
 *
 * **It replaces the access whole.** The lists are the complete selection, not
 * additions — to take `ALA` out of `["ALA", "NQZ"]` the body says `["NQZ"]`. The server
 * reads only these three fields.
 */
export interface FlightAccessCard {
  restricted: boolean;
  airports: string[];
  airlines: string[];
}

/** What a user with no saved access has: every flight. */
export const unrestrictedAccess = (userId: number): FlightAccess => ({
  userId,
  restricted: false,
  airports: [],
  airlines: [],
  updated: null,
  updatedBy: null,
});

/**
 * A code list the way the server stores it: trimmed, upper case, no blanks, no
 * repeats, sorted. Applied to what is sent as well as to what comes back, so two
 * selections that mean the same thing also compare equal.
 */
export const codeList = (codes: readonly (string | null | undefined)[] | null | undefined): string[] =>
  [
    ...new Set(
      (codes ?? []).flatMap((code) => {
        const upper = (code ?? '').trim().toUpperCase();
        return upper === '' ? [] : [upper];
      }),
    ),
  ].sort();

/**
 * One access as the server returned it, made safe to read.
 *
 * The lists are documented as always present, but a `null` where an array belongs
 * would throw on the first `.map` of a screen that only wanted to show a summary, so
 * they are read through `codeList`. `userId` falls back to the id that was asked for:
 * `/user/me/flight-access` and a delete have no id in the request to compare with.
 */
export const readAccess = (raw: unknown, userId: number): FlightAccess => {
  if (raw === null || typeof raw !== 'object') return unrestrictedAccess(userId);

  const value = raw as Partial<Record<keyof FlightAccess, unknown>>;
  const restricted = value.restricted === true;

  return {
    userId: typeof value.userId === 'number' ? value.userId : userId,
    restricted,
    // Kept only while restricted: an unrestricted access with codes in it describes
    // nothing, and the server clears them on save anyway.
    airports: restricted ? codeList(asCodes(value.airports)) : [],
    airlines: restricted ? codeList(asCodes(value.airlines)) : [],
    updated: typeof value.updated === 'string' ? value.updated : null,
    updatedBy: typeof value.updatedBy === 'string' ? value.updatedBy : null,
  };
};

const asCodes = (value: unknown): string[] =>
  Array.isArray(value) ? value.filter((code): code is string => typeof code === 'string') : [];

/**
 * The station a board shows, held to the stations a reader may pick.
 *
 * `only` is the reader's airports when their access names any, and `null` otherwise —
 * then every station is theirs and the URL is taken as it stands.
 *
 * Inside an access, a station from the URL that is not one of `only` — an old link,
 * a colleague's link — is not kept, because the dropdown cannot show it and the board
 * would then be filtered by a value nobody can see. It falls back to the default.
 */
export const stationWithin = (asked: string, only: readonly string[] | null): string => {
  if (only === null) return asked;

  const code = asked.trim().toUpperCase();
  return only.includes(code) ? code : defaultStation(only);
};

/**
 * The station a board opens on.
 *
 * One airport in the access IS the board: there is no other station to choose, and
 * "all" would show the far-end leg of every flight too — the Almaty departure of
 * `ALA → NQZ` on an Astana reader's board. Several airports, or none named, open on
 * all of them.
 */
export const defaultStation = (only: readonly string[] | null): string =>
  only !== null && only.length === 1 ? (only[0] ?? '') : '';

/**
 * Whether this access narrows what the user sees.
 *
 * `null` is "not known yet" and reads as not restricted — the safe direction for a
 * screen that only DESCRIBES the access, because the server enforces it either way.
 */
export const isRestricted = (access: FlightAccess | null | undefined): boolean =>
  access?.restricted === true;
